/**
 * ConfirmModal
 * 
 * A styled modal for confirmation dialogs (delete, dangerous actions).
 * Replaces browser confirm() calls with a consistent, themed UI.
 */

import React from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import Modal from './Modal';

type ConfirmModalVariant = 'danger' | 'warning';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  title: string;
  message: string;
  variant?: ConfirmModalVariant;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
}

const variantConfig: Record<ConfirmModalVariant, { 
  icon: React.ElementType; 
  iconColor: string; 
  buttonClass: string;
}> = {
  danger: { 
    icon: Trash2, 
    iconColor: 'text-rose-400',
    buttonClass: 'bg-rose-600 hover:bg-rose-700 text-white'
  },
  warning: { 
    icon: AlertTriangle, 
    iconColor: 'text-amber-400',
    buttonClass: 'bg-amber-600 hover:bg-amber-700 text-white'
  },
};

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  variant = 'danger',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
}) => {
  const config = variantConfig[variant];
  const IconComponent = config.icon;

  const handleConfirm = async () => {
    await onConfirm();
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title}>
      <div className="space-y-4">
        <div className="text-center">
          <IconComponent size={48} className={`mx-auto mb-4 ${config.iconColor}`} />
          <p className="text-slate-300">{message}</p>
        </div>
        <div className="flex gap-3 pt-2">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2 bg-slate-700 text-slate-200 rounded hover:bg-slate-600 font-medium transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isLoading}
            className={`flex-1 py-2 rounded font-bold transition-colors disabled:opacity-50 ${config.buttonClass}`}
          >
            {isLoading ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;
